import { Link } from '@tanstack/react-router';
import { Info, Settings2 } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ActorLabel } from '~/components/common/actor-label';
import { Button } from '~/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/components/ui/tooltip';
import { formatUsd } from '~/features/cost-estimate/utils/format-usd';
import { useAuthStore } from '~/store/auth.store';
import { diffInMs, formatDateTime, formatDurationMs, nowISO } from '~/utils/date';

import { useRunScorecard } from '../hooks/use-run-content';
import { useFunnelComparison } from '../hooks/use-run-funnel';
import { useRunCostEstimate } from '../hooks/use-run-ledger';
import { useScheduleDetail } from '../hooks/use-schedules-queries';
import { toCostEstimateView } from '../mappers/cost-gate.mapper';
import type { RunProgress, RunRead } from '../types/run.api.types';
import { catalogLabel } from '../utils/catalog-labels';
import { formatCount } from '../utils/run-format';
import { isRunLive } from '../utils/run-status';
import { reachedContent } from '../utils/run-tabs';

import { BatchSidePanel } from './batch-side-panel';
import { ConnectorMark } from './connector-mark';
import { RunIds } from './run-ids-card';
import { RunSettingsCard } from './run-settings-card';

interface Props {
  runId: string;
  run: RunRead | undefined;
  progress: RunProgress;
}

/**
 * The batch header's two icon buttons: ⓘ "About this batch" opens the facts in a popover, ⚙
 * "Settings" opens how the batch was set up in a side sheet. They replace the Overview's facts
 * column, so both are reachable from every batch tab and the step list gets the full width.
 */
export function RunFactsActions({ runId, run, progress }: Props) {
  const { t } = useTranslation('catalog-enrichment-run');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const isSuperAdmin = useAuthStore((s) => s.user?.isSuperAdmin ?? false);
  const live = isRunLive(progress.runStatus);

  return (
    <TooltipProvider>
      <Popover>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="size-9 cursor-pointer"
                aria-label={t('facts.title')}
                data-testid="catalog-enrichment-run-facts-button"
              >
                <Info aria-hidden="true" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>{t('facts.title')}</TooltipContent>
        </Tooltip>
        <PopoverContent
          align="end"
          className="w-96 max-w-[calc(100vw-2rem)] p-5"
          data-testid="catalog-enrichment-run-facts-popover"
        >
          {/* Mounted only while open, so its cost / score / comparison reads wait for a click. */}
          <RunFactsList runId={runId} run={run} progress={progress} />
        </PopoverContent>
      </Popover>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className="size-9 cursor-pointer"
            aria-label={t('facts.settings')}
            data-testid="catalog-enrichment-run-facts-settings-button"
            onClick={() => {
              setSettingsOpen(true);
            }}
          >
            <Settings2 aria-hidden="true" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('facts.settings')}</TooltipContent>
      </Tooltip>

      <BatchSidePanel
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        title={t('sideRail.setup')}
        width="wide"
        footer={
          live ? undefined : (
            <Button asChild variant="outline" className="cursor-pointer">
              <Link
                to="/catalog-enrichment/runs/new"
                search={{ copyFrom: runId }}
                data-testid="catalog-enrichment-run-facts-copy-button"
              >
                {t('facts.copyIntoNew')}
              </Link>
            </Button>
          )
        }
      >
        <div className="flex flex-col gap-6">
          <RunSettingsCard runId={runId} run={run} progress={progress} embedded />
          {/* Raw ids are a support aid, not customer UI (guide part 2 §2.6). */}
          {isSuperAdmin ? <RunIds runId={runId} run={run} progress={progress} /> : null}
        </div>
      </BatchSidePanel>
    </TooltipProvider>
  );
}

/**
 * "About this batch" (guide part 2 §4.1): one plain definition list — store, who started it and
 * when, how long it took, its schedule or experiment, what it retried, cost, content score and how
 * it compares with the last batch — showing only rows that are known.
 */
function RunFactsList({ runId, run, progress }: Props) {
  const { t } = useTranslation('catalog-enrichment-run');
  const { t: tCatalog } = useTranslation('catalog-enrichment');
  const live = isRunLive(progress.runStatus);
  const schedule = useScheduleDetail(run?.scheduleId ?? null).data;
  const hasEnrichment = progress.stages.some((s) => s.stage === 'ENRICHMENT');
  const estimateRead = useRunCostEstimate(runId, hasEnrichment).data;
  const cost = estimateRead ? (toCostEstimateView(estimateRead).total?.avgUsd ?? null) : null;
  const scorecard = useRunScorecard(runId, reachedContent(progress)).data;
  const comparison = useFunnelComparison(runId, true).data;

  const connector = progress.connectorCode ?? run?.connectorCode ?? null;
  const startedAt = progress.startedAt ?? run?.startedAt ?? null;
  const endedAt = progress.completedAt ?? (live ? nowISO() : null);
  const creator = run?.audit.createdBy ?? null;
  const before = scorecard?.pre?.average ?? null;
  const after = scorecard?.post?.average ?? null;
  const productsNow = comparison?.current.products ?? null;
  const productsThen = comparison?.previous?.products ?? null;

  const rows: { key: string; label: string; value: ReactNode }[] = [];
  if (connector) {
    rows.push({
      key: 'store',
      label: t('facts.store'),
      value: (
        <span className="inline-flex items-center gap-1.5">
          <ConnectorMark code={connector} />
          {catalogLabel(tCatalog, 'connector', connector)}
        </span>
      ),
    });
  }
  if (run?.scheduleId) {
    rows.push({
      key: 'schedule',
      label: t('facts.schedule'),
      value: (
        <span className="flex flex-col">
          <Link
            to="/catalog-enrichment/schedules/$scheduleId"
            params={{ scheduleId: run.scheduleId }}
            className="text-primary cursor-pointer hover:underline"
            data-testid="catalog-enrichment-run-facts-schedule-link"
          >
            {run.scheduleName ?? t('facts.openSchedule')}
          </Link>
          {schedule?.nextRunAt && !schedule.pausedAt ? (
            <span className="text-secondary-text text-xs">
              {t('facts.nextRun', { at: formatDateTime(schedule.nextRunAt) })}
            </span>
          ) : null}
        </span>
      ),
    });
  } else if (creator?.actorId || progress.createdBy) {
    rows.push({
      key: 'startedBy',
      label: t('facts.startedBy'),
      value: (
        <ActorLabel
          actor={{
            id: creator?.actorId ?? null,
            displayName: creator?.handle ?? progress.createdBy ?? '',
            kind: creator?.kind ?? 'USER',
            email: null,
            avatarUrl: null,
            resolved: !!(creator?.handle ?? progress.createdBy),
          }}
        />
      ),
    });
  }
  if (startedAt) {
    rows.push({ key: 'started', label: t('facts.started'), value: formatDateTime(startedAt) });
  }
  if (startedAt && endedAt) {
    rows.push({
      key: 'took',
      label: t(live ? 'facts.runningFor' : 'facts.took'),
      value: formatDurationMs(diffInMs(startedAt, endedAt)),
    });
  }
  if (run?.parentRunId) {
    rows.push({
      key: 'retryOf',
      label: t('facts.retryOf'),
      value: (
        <Link
          to="/catalog-enrichment/runs/$runId"
          params={{ runId: run.parentRunId }}
          className="text-primary cursor-pointer hover:underline"
          data-testid="catalog-enrichment-run-facts-parent-link"
        >
          {t('facts.openParent')}
        </Link>
      ),
    });
  }
  if (cost !== null) {
    rows.push({
      key: 'cost',
      label: t('facts.cost'),
      value: t('facts.costEstimated', { amount: formatUsd(cost) }),
    });
  }
  if (before !== null || after !== null) {
    rows.push({
      key: 'score',
      label: t('facts.score'),
      value:
        before !== null && after !== null
          ? `${String(Math.round(before))} → ${String(Math.round(after))}`
          : t('facts.scoreBefore', { score: Math.round(before ?? after ?? 0) }),
    });
  }
  if (comparison?.previousRunId && productsNow !== null && productsThen !== null) {
    const delta = productsNow - productsThen;
    rows.push({
      key: 'vsLast',
      label: t('facts.vsLast'),
      value: (
        <Link
          to="/catalog-enrichment/runs/$runId"
          params={{ runId: comparison.previousRunId }}
          className="cursor-pointer hover:underline"
          data-testid="catalog-enrichment-run-facts-previous-link"
        >
          {delta === 0
            ? t('facts.vsLastSame')
            : t('facts.vsLastDelta', {
                delta: `${delta > 0 ? '+' : '−'}${formatCount(Math.abs(delta))}`,
              })}
        </Link>
      ),
    });
  }

  return (
    <section
      data-testid="catalog-enrichment-run-facts"
      aria-label={t('facts.title')}
      className="flex flex-col gap-3"
    >
      <h2 className="text-foreground text-sm font-semibold">{t('facts.title')}</h2>
      <dl className="grid grid-cols-[minmax(0,7rem)_minmax(0,1fr)] gap-x-4 gap-y-2.5 text-sm">
        {rows.map((row) => (
          <div
            key={row.key}
            className="contents"
            data-testid={`catalog-enrichment-run-facts-${row.key}`}
          >
            <dt className="text-secondary-text">{row.label}</dt>
            <dd className="text-foreground min-w-0 break-words tabular-nums">{row.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
